import { useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { tenantsService, TenantView } from '../../services/tenants/tenants.service';
import { adminWoltService } from '../../services/admin-wolt/admin-wolt.service';
import { adminTenbisService } from '../../services/admin-tenbis/admin-tenbis.service';
import { notifyTenbisMenuSync } from '../../utils/tenbis-menu-sync-notify';
import type { TenbisDraft } from './components/TenantTenbisBlock';
import { useAdminTenants } from '../../contexts/AdminEventsProvider';
import { useAuth } from '../../contexts/AuthProvider';
import { notifyWoltMenuSync } from '../../utils/wolt-menu-sync-notify';
import {
  INITIAL_EXPANDED_TENANT_ID,
  INITIAL_TENANT_NAME,
  getTenantsCountLabel,
} from './tenants-page.types';
import {
  addUpdatingTenantId,
  buildCreateTenantPayload,
  buildToggleActivePayload,
  buildToggleTenantImpact,
  normalizeCompanyName,
  removeUpdatingTenantId,
  toggleExpandedTenant,
} from './tenants-page.utils';

// A pending logo edit for one row: a picked file (with local preview) or a staged removal.
type StagedPhotoAction =
  | { kind: 'pick'; file: File; previewUrl: string }
  | { kind: 'remove' };

type WoltConfirmPrompt = { title: string; message: string; confirmLabel: string };
type WoltConfirm = WoltConfirmPrompt & { run: () => Promise<void> };

/**
 * Architectural summary: Encapsulates TenantsPage state + action handlers
 * (create modal flow, activation toggle, row expansion), while consuming the
 * canonical tenants snapshot from AdminEventsProvider.
 */
export function useTenantsPageController() {
  const { tenants, isLoading, error: tenantsError } = useAdminTenants();
  const { refreshAvailableTenants } = useAuth();
  const [companyName, setCompanyName] = useState(INITIAL_TENANT_NAME);
  const [isCreating, setIsCreating] = useState(false);
  const [updatingTenantIds, setUpdatingTenantIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [expandedTenantId, setExpandedTenantId] = useState<number | null>(INITIAL_EXPANDED_TENANT_ID);
  const [ownerEmailDrafts, setOwnerEmailDrafts] = useState<Record<number, string>>({});
  const [isTestDrafts, setIsTestDrafts] = useState<Record<number, boolean>>({});
  const [restigoBranchDrafts, setRestigoBranchDrafts] = useState<Record<number, string>>({});
  const [restigoKeyDrafts, setRestigoKeyDrafts] = useState<Record<number, string>>({});
  const [ontopoKeyDrafts, setOntopoKeyDrafts] = useState<Record<number, string>>({});
  const [woltVenueDrafts, setWoltVenueDrafts] = useState<Record<number, string>>({});
  const [woltConfirm, setWoltConfirm] = useState<WoltConfirm | null>(null);
  const [tenbisDrafts, setTenbisDrafts] = useState<Record<number, Partial<TenbisDraft>>>({});
  const [photoDrafts, setPhotoDrafts] = useState<Record<number, StagedPhotoAction>>({});
  const panelSaveLocksRef = useRef<Set<number>>(new Set());

  const displayedError = error ?? tenantsError;
  const tenantsCountLabel = useMemo(() => getTenantsCountLabel(tenants), [tenants]);

  /**
   * Architectural summary: Submits new tenant from modal and relies on SSE
   * provider snapshot to reflect the canonical row.
   */
  const handleCreateTenant = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedName = normalizeCompanyName(companyName);
    if (!normalizedName) {
      setError('שם החברה נדרש');
      return;
    }

    setIsCreating(true);
    setError(null);
    try {
      await tenantsService.create(buildCreateTenantPayload(companyName));
      setCompanyName(INITIAL_TENANT_NAME);
      setIsCreateOpen(false);
    } catch (createError: unknown) {
      setError(createError instanceof Error ? createError.message : 'יצירת החברה נכשלה');
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Architectural summary: Toggles tenant active state, requesting impact
   * details before deactivation to support explicit confirmation.
   */
  const toggleTenantActive = async (tenant: TenantView) => {
    setError(null);

    if (tenant.isActive) {
      try {
        const impact = await tenantsService.deactivationImpact(tenant.id);
        const details = buildToggleTenantImpact(tenant, impact);
        if (!window.confirm([details.title, ...details.lines].join('\n'))) {
          return;
        }
      } catch (impactError: unknown) {
        setError(impactError instanceof Error ? impactError.message : 'שליפת השפעת ההשבתה נכשלה');
        return;
      }
    }

    setUpdatingTenantIds((previous) => addUpdatingTenantId(previous, tenant.id));
    try {
      await tenantsService.updateActive(tenant.id, buildToggleActivePayload(tenant));
    } catch (updateError: unknown) {
      setError(updateError instanceof Error ? updateError.message : 'עדכון סטטוס החברה נכשל');
    } finally {
      setUpdatingTenantIds((previous) => removeUpdatingTenantId(previous, tenant.id));
    }
  };

  // Stages a picked logo with a local preview; nothing hits the network until "שמור הכל".
  const stageTenantPhoto = (tenant: TenantView, file: File | null) => {
    setError(null);
    setPhotoDrafts((current) => {
      const previous = current[tenant.id];
      if (previous?.kind === 'pick') {
        URL.revokeObjectURL(previous.previewUrl);
      }
      if (!file) {
        const next = { ...current };
        delete next[tenant.id];
        return next;
      }
      return {
        ...current,
        [tenant.id]: { kind: 'pick', file, previewUrl: URL.createObjectURL(file) },
      };
    });
  };

  // Stages a logo removal (placeholder preview); the delete only fires on "שמור הכל".
  const stageRemoveTenantPhoto = (tenant: TenantView) => {
    setError(null);
    setPhotoDrafts((current) => {
      const previous = current[tenant.id];
      if (previous?.kind === 'pick') {
        URL.revokeObjectURL(previous.previewUrl);
      }
      return { ...current, [tenant.id]: { kind: 'remove' } };
    });
  };

  const getStagedPhotoFile = (tenant: TenantView): File | null => {
    const staged = photoDrafts[tenant.id];
    return staged?.kind === 'pick' ? staged.file : null;
  };

  // Preview src: staged pick -> local blob, staged removal -> none, else the saved photoUrl.
  const getTenantPhotoPreviewUrl = (tenant: TenantView): string | null => {
    const staged = photoDrafts[tenant.id];
    if (staged?.kind === 'pick') {
      return staged.previewUrl;
    }
    if (staged?.kind === 'remove') {
      return null;
    }
    return tenant.photoUrl ?? null;
  };

  const showRemovePhotoControl = (tenant: TenantView): boolean =>
    Boolean(tenant.photoUrl) && !photoDrafts[tenant.id];

  /**
   * Architectural summary: Opens create modal with clean draft state.
   */
  const openCreateModal = () => {
    setError(null);
    setCompanyName(INITIAL_TENANT_NAME);
    setIsCreateOpen(true);
  };

  /**
   * Architectural summary: Toggles one tenant row expanded state.
   */
  const onToggleExpanded = (tenantId: number) => {
    setExpandedTenantId((current) => toggleExpandedTenant(current, tenantId));
  };

  const setTenantOwnerEmailDraft = (tenantIdPk: number, value: string) => {
    setOwnerEmailDrafts((current) => ({ ...current, [tenantIdPk]: value }));
  };

  const getTenantOwnerEmailDraft = (tenant: TenantView): string => {
    return ownerEmailDrafts[tenant.id] ?? tenant.ownerEmail ?? '';
  };

  const isTenantEmailChanged = (tenant: TenantView): boolean =>
    getTenantOwnerEmailDraft(tenant).trim() !== (tenant.ownerEmail ?? '').trim();

  const getTenantIsTestDraft = (tenant: TenantView): boolean =>
    isTestDrafts[tenant.id] ?? tenant.isTest;

  const setTenantIsTestDraft = (tenantIdPk: number, value: boolean) => {
    setIsTestDrafts((current) => ({ ...current, [tenantIdPk]: value }));
  };

  const isTenantIsTestChanged = (tenant: TenantView): boolean =>
    getTenantIsTestDraft(tenant) !== tenant.isTest;

  const hasTenantPanelChanges = (tenant: TenantView): boolean =>
    isTenantEmailChanged(tenant)
    || isTenantRestigoChanged(tenant)
    || isTenantIsTestChanged(tenant)
    || isTenantOntopoKeyChanged(tenant)
    || isTenantWoltVenueChanged(tenant)
    || isTenantTenbisChanged(tenant)
    || Boolean(photoDrafts[tenant.id]);

  const setTenantRestigoBranchDraft = (tenantIdPk: number, value: string) => {
    setRestigoBranchDrafts((current) => ({ ...current, [tenantIdPk]: value }));
  };

  const getTenantRestigoBranchDraft = (tenant: TenantView): string =>
    restigoBranchDrafts[tenant.id] ?? (tenant.restigoBranchId != null ? String(tenant.restigoBranchId) : '');

  const setTenantRestigoKeyDraft = (tenantIdPk: number, value: string) => {
    setRestigoKeyDrafts((current) => ({ ...current, [tenantIdPk]: value }));
  };

  // Undefined draft = untouched (keeps masked key); a string draft (incl. empty) is a real edit.
  const getTenantRestigoKeyDraft = (tenant: TenantView): string | undefined => restigoKeyDrafts[tenant.id];

  const isTenantRestigoBranchChanged = (tenant: TenantView): boolean =>
    getTenantRestigoBranchDraft(tenant).trim() !== (tenant.restigoBranchId != null ? String(tenant.restigoBranchId) : '');

  const isTenantRestigoKeyChanged = (tenant: TenantView): boolean =>
    getTenantRestigoKeyDraft(tenant) !== undefined;

  const isTenantRestigoChanged = (tenant: TenantView): boolean =>
    isTenantRestigoBranchChanged(tenant) || isTenantRestigoKeyChanged(tenant);

  const setTenantOntopoKeyDraft = (tenantIdPk: number, value: string) => {
    setOntopoKeyDrafts((current) => ({ ...current, [tenantIdPk]: value }));
  };

  // Raw key is never returned; draft stays undefined until typed, so the field shows only the mask.
  const getTenantOntopoKeyDraft = (tenant: TenantView): string =>
    ontopoKeyDrafts[tenant.id] ?? '';

  const isTenantOntopoKeyChanged = (tenant: TenantView): boolean =>
    ontopoKeyDrafts[tenant.id] !== undefined;

  const setTenantWoltVenueDraft = (tenantIdPk: number, value: string) => {
    setWoltVenueDrafts((current) => ({ ...current, [tenantIdPk]: value }));
  };

  const getTenantWoltVenueDraft = (tenant: TenantView): string =>
    woltVenueDrafts[tenant.id] ?? (tenant.woltVenueId ?? '');

  const isTenantWoltVenueChanged = (tenant: TenantView): boolean =>
    getTenantWoltVenueDraft(tenant).trim() !== (tenant.woltVenueId ?? '');

  const setTenantTenbisDraft = (tenantIdPk: number, patch: Partial<TenbisDraft>) => {
    setTenbisDrafts((current) => ({ ...current, [tenantIdPk]: { ...current[tenantIdPk], ...patch } }));
  };

  // The password is write-only: its draft starts empty and only a typed value is ever sent.
  const getTenantTenbisDraft = (tenant: TenantView): TenbisDraft => ({
    userName: tenbisDrafts[tenant.id]?.userName ?? tenant.tenbisUserName ?? '',
    password: tenbisDrafts[tenant.id]?.password ?? '',
    resId: tenbisDrafts[tenant.id]?.resId ?? tenant.tenbisResId ?? '',
  });

  // Diff-only payload for PATCH /tenbis/tenants/:id/config; null = nothing changed.
  const buildTenbisDiff = (tenant: TenantView) => {
    const draft = getTenantTenbisDraft(tenant);
    const diff: { userName?: string; password?: string; resId?: string } = {};
    if (draft.userName.trim() !== (tenant.tenbisUserName ?? '') && draft.userName.trim()) diff.userName = draft.userName.trim();
    if (draft.password.length > 0) diff.password = draft.password;
    if (draft.resId.trim() !== (tenant.tenbisResId ?? '') && draft.resId.trim()) diff.resId = draft.resId.trim();
    return Object.keys(diff).length > 0 ? diff : null;
  };

  const isTenantTenbisChanged = (tenant: TenantView): boolean => buildTenbisDiff(tenant) !== null;

  const runProviderAction = async (tenant: TenantView, action: () => Promise<void>, failureMessage: string) => {
    if (panelSaveLocksRef.current.has(tenant.id)) {
      return;
    }
    panelSaveLocksRef.current.add(tenant.id);
    setError(null);
    setUpdatingTenantIds((previous) => addUpdatingTenantId(previous, tenant.id));
    try {
      await action();
    } catch (actionError: unknown) {
  setError(actionError instanceof Error ? actionError.message : failureMessage);
    } finally {
      panelSaveLocksRef.current.delete(tenant.id);
      setUpdatingTenantIds((previous) => removeUpdatingTenantId(previous, tenant.id));
    }
  };

  // Irreversible Wolt actions go through the shared ConfirmModal; the run fires on אישור only.
  const requestWoltConfirm = (tenant: TenantView, prompt: WoltConfirmPrompt, action: () => Promise<void>) =>
    setWoltConfirm({ ...prompt, run: () => runProviderAction(tenant, action, 'פעולת וולט נכשלה') });

  const confirmWoltAction = async () => {
    const pending = woltConfirm;
    setWoltConfirm(null);
    await pending?.run();
  };

  const cancelWoltConfirm = () => setWoltConfirm(null);

  const disconnectWolt = (tenant: TenantView) =>
    requestWoltConfirm(
      tenant,
      { title: `לנתק את ${tenant.companyName} מוולט?`, message: 'יידרש חיבור מחדש דרך וולט.', confirmLabel: 'נתק' },
      () => adminWoltService.disconnect(tenant.id),
    );

  const syncWoltMenu = (tenant: TenantView) =>
    requestWoltConfirm(
      tenant,
      { title: `לסנכרן את התפריט של ${tenant.companyName} לוולט?`, message: 'התפריט הקיים בוולט יוחלף.', confirmLabel: 'סנכרן' },
      async () => notifyWoltMenuSync(await adminWoltService.syncMenu(tenant.id)),
    );

  const testTenbisLogin = (tenant: TenantView) =>
    runProviderAction(tenant, async () => {
      const result = await adminTenbisService.testLogin(tenant.id);
      notifications.show({ color: 'green', title: 'תן ביס', message: `מחובר: ${result.resName}`, autoClose: 4000 });
    }, 'פעולת תן ביס נכשלה');

  const disconnectTenbis = (tenant: TenantView) => {
    if (panelSaveLocksRef.current.has(tenant.id)) return;
    if (!window.confirm(`לנתק את ${tenant.companyName} מתן ביס?`)) {
      return;
    }
    void runProviderAction(tenant, () => adminTenbisService.disconnect(tenant.id), 'פעולת תן ביס נכשלה');
  };

  // Full replace (deleteItems=true) wipes whatever is not sent, so the prompt names the tenant.
  const syncTenbisMenu = (tenant: TenantView) => {
    if (panelSaveLocksRef.current.has(tenant.id)) return Promise.resolve();
    if (!window.confirm(`לסנכרן את תפריט ${tenant.companyName} לתן ביס? פריטים שלא נשלחים יימחקו שם.`)) {
      return Promise.resolve();
    }
    return runProviderAction(tenant, async () => notifyTenbisMenuSync(await adminTenbisService.syncMenu(tenant.id)), 'פעולת תן ביס נכשלה');
  };

  // Saves the whole panel in one action: diff-only email PATCH + staged settings/photo updates.
  const saveTenantPanel = async (tenant: TenantView) => {
    if (panelSaveLocksRef.current.has(tenant.id) || !hasTenantPanelChanges(tenant)) {
      return;
    }
    panelSaveLocksRef.current.add(tenant.id);
    setError(null);
    setUpdatingTenantIds((previous) => addUpdatingTenantId(previous, tenant.id));
    try {
      if (isTenantEmailChanged(tenant)) {
        const ownerEmailRaw = getTenantOwnerEmailDraft(tenant).trim();
        await tenantsService.updateOwnerEmail(tenant.id, {
          ownerEmail: ownerEmailRaw.length > 0 ? ownerEmailRaw : null,
        });
      }
      if (isTenantIsTestChanged(tenant)) {
        await tenantsService.updateIsTest(tenant.id, getTenantIsTestDraft(tenant));
      };
      if (isTenantRestigoChanged(tenant)) {
        const payload: { restigoBranchId?: number | null; restigoKey?: string | null } = {};
        if (isTenantRestigoBranchChanged(tenant)) {
          const branchRaw = getTenantRestigoBranchDraft(tenant).trim();
          if (branchRaw.length === 0) {
            payload.restigoBranchId = null;
          } else {
            const parsed = Number(branchRaw);
            // Guard junk like "-": NaN would serialize to null and silently CLEAR the stored id.
            if (!Number.isInteger(parsed) || parsed <= 0) {
              setError('מזהה סניף רסטיגו חייב להיות מספר שלם חיובי');
              return;
            }
            payload.restigoBranchId = parsed;
          }
        }
        if (isTenantRestigoKeyChanged(tenant)) {
          const keyRaw = (getTenantRestigoKeyDraft(tenant) ?? '').trim();
          payload.restigoKey = keyRaw.length > 0 ? keyRaw : null;
        }
        await tenantsService.updateRestigoConfig(tenant.id, payload);
        setRestigoBranchDrafts((current) => {
          const next = { ...current };
          delete next[tenant.id];
          return next;
        });
        setRestigoKeyDrafts((current) => {
          const next = { ...current };
          delete next[tenant.id];
          return next;
        });
      }
      if (isTenantOntopoKeyChanged(tenant)) {
        const ontopoKeyRaw = getTenantOntopoKeyDraft(tenant).trim();
        await tenantsService.updateOntopoKey(tenant.id, ontopoKeyRaw.length > 0 ? ontopoKeyRaw : null);
        setOntopoKeyDrafts((current) => {
          const next = { ...current };
          delete next[tenant.id];
          return next;
        });
      }
      if (isTenantWoltVenueChanged(tenant)) {
        const venueRaw = getTenantWoltVenueDraft(tenant).trim();
        await adminWoltService.updateTenantConfig(tenant.id, { woltVenueId: venueRaw.length > 0 ? venueRaw : null });
        setWoltVenueDrafts((current) => {
          const next = { ...current };
          delete next[tenant.id];
          return next;
        });
      }
      const tenbisDiff = buildTenbisDiff(tenant);
      if (tenbisDiff) {
        await adminTenbisService.updateTenantConfig(tenant.id, tenbisDiff);
        setTenbisDrafts((current) => {
          const next = { ...current };
          delete next[tenant.id];
          return next;
        });
      }
      const staged = photoDrafts[tenant.id];
      if (staged?.kind === 'pick') {
        await tenantsService.uploadPhoto(tenant.id, staged.file);
        URL.revokeObjectURL(staged.previewUrl);
      } else if (staged?.kind === 'remove') {
        await tenantsService.removePhoto(tenant.id);
      }
      setPhotoDrafts((current) => {
        const next = { ...current };
        delete next[tenant.id];
        return next;
      });
      if (staged) {
        // Header logo reads AuthProvider /auth/tenants (outside admin SSE) — refetch it.
        try {
          await refreshAvailableTenants();
        } catch {
          // Best-effort Header sync; the save itself already succeeded.
        }
      }
    } catch (saveError: unknown) {
      setError(saveError instanceof Error ? saveError.message : 'שמירת פרטי החברה נכשלה');
    } finally {
      panelSaveLocksRef.current.delete(tenant.id);
      setUpdatingTenantIds((previous) => removeUpdatingTenantId(previous, tenant.id));
    }
  };

  return {
    tenants,
    isLoading,
    companyName,
    isCreating,
    updatingTenantIds,
    isCreateOpen,
    expandedTenantId,
    displayedError,
    tenantsCountLabel,
    setCompanyName,
    setIsCreateOpen,
    handleCreateTenant,
    toggleTenantActive,
    openCreateModal,
    onToggleExpanded,
    getTenantOwnerEmailDraft,
    setTenantOwnerEmailDraft,
    getTenantIsTestDraft,
    setTenantIsTestDraft,
    getTenantRestigoBranchDraft,
    setTenantRestigoBranchDraft,
    getTenantRestigoKeyDraft,
    setTenantRestigoKeyDraft,
    getTenantOntopoKeyDraft,
    setTenantOntopoKeyDraft,
    getTenantWoltVenueDraft,
    setTenantWoltVenueDraft,
    disconnectWolt,
    syncWoltMenu,
    woltConfirm,
    confirmWoltAction,
    cancelWoltConfirm,
    getTenantTenbisDraft,
    setTenantTenbisDraft,
    testTenbisLogin,
    disconnectTenbis,
    syncTenbisMenu,
    getStagedPhotoFile,
    getTenantPhotoPreviewUrl,
    showRemovePhotoControl,
    hasTenantPanelChanges,
    stageTenantPhoto,
    stageRemoveTenantPhoto,
    saveTenantPanel,
  };
}
